import { onLCP, onFCP, onCLS, onINP, onTTFB, type Metric } from 'web-vitals';
import { TelemetryEventType } from './telemetry-models';
import { BaseTracker } from './base-tracker';
import { getElementSelector, getLoadState } from './utils/telemetry-utils';
import type {
  FeatureFlags,
  MetricRating,
  PageLoadMetric,
  TelemetryEvent,
  WebVitalMetric,
  WebVitalType,
} from './telemetry-models';

/**
 * Attribution interfaces for web-vitals metrics
 * These provide debugging context for performance issues
 */
interface LCPAttribution {
  element?: Element;
  url?: string;
  timeToFirstByte?: number;
  resourceLoadDelay?: number;
  resourceLoadTime?: number;
  elementRenderDelay?: number;
}

interface CLSAttribution {
  largestShiftTarget?: Element;
  largestShiftValue?: number;
  largestShiftTime?: number;
  loadState?: string;
}

interface INPAttribution {
  interactionTarget?: Element;
  interactionType?: string;
  interactionTime?: number;
  processsingDuration?: number;
  presentationDelay?: number;
  loadState?: string;
}

interface FCPAttribution {
  timeToFirstByte?: number;
  firstByteToFCP?: number;
  loadState?: string;
}

interface TTFBAttribution {
  waitingTime?: number;
  dnsTime?: number;
  connectionTime?: number;
  requestTime?: number;
}

type MetricAttribution = LCPAttribution | CLSAttribution | INPAttribution | FCPAttribution | TTFBAttribution;

interface MetricWithAttribution extends Metric {
  attribution?: MetricAttribution;
}

/**
 * Tracks Core Web Vitals metrics using the official web-vitals library.
 * @see https://github.com/GoogleChrome/web-vitals
 */
export class WebVitalsTracker extends BaseTracker {
  private _features: FeatureFlags;

  constructor(features: FeatureFlags, onEvent: (event: TelemetryEvent) => void, enabledLogging = false) {
    super(onEvent, enabledLogging);
    this._features = features;
  }

  /**
   * Start tracking Web Vitals
   */
  public start(): void {
    if (!this._features.hasPerformanceObserver) {
      this._log('PerformanceObserver not available, skipping Web Vitals tracking');
      return;
    }

    try {
      // Track all Core Web Vitals using the official library
      onLCP((metric) => this._handleMetric(metric), { reportAllChanges: true });
      onFCP((metric) => this._handleMetric(metric));
      onCLS((metric) => this._handleMetric(metric), { reportAllChanges: false });
      onINP((metric) => this._handleMetric(metric), { reportAllChanges: false });
      onTTFB((metric) => this._handleMetric(metric));

      this._setupPageLoad();
      this._log('Web Vitals tracking started');
    } catch (error) {
      this._log('ERROR: Failed to setup Web Vitals', error);
    }
  }

  /**
   * Stop tracking and clean up
   * Note: The web-vitals library doesn't provide cleanup functions,
   * so observers will continue until page unload
   */
  public stop(): void {
    this._log('Web Vitals tracking stopped (observers remain active)');
  }

  /**
   * Handle metric updates from web-vitals library
   */
  private _handleMetric(metric: Metric): void {
    const webVital: WebVitalMetric = {
      name: metric.name as WebVitalType,
      value: metric.value,
      rating: metric.rating as MetricRating,
      timestamp: Date.now(),
      element: this._getElementFromAttribution(metric),
      load_state: getLoadState(),
    };

    // Map Web Vital name to specific event type
    const eventType = this._getEventTypeForMetric(metric.name);
    this._onEvent({ type: eventType, data: webVital });
    this._log(`${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`);
  }

  /**
   * Map Web Vital metric name to specific telemetry event type
   */
  private _getEventTypeForMetric(metricName: string): TelemetryEventType {
    switch (metricName) {
      case 'LCP':
        return TelemetryEventType.WEB_VITAL_LCP;
      case 'FCP':
        return TelemetryEventType.WEB_VITAL_FCP;
      case 'CLS':
        return TelemetryEventType.WEB_VITAL_CLS;
      case 'INP':
        return TelemetryEventType.WEB_VITAL_INP;
      case 'TTFB':
        return TelemetryEventType.WEB_VITAL_TTFB;
      default:
        // Fallback to LCP if unknown metric
        this._log(`WARN: Unknown Web Vital metric: ${metricName}`);
        return TelemetryEventType.WEB_VITAL_LCP;
    }
  }

  /**
   * Extract element information from metric attribution data
   */
  private _getElementFromAttribution(metric: Metric): string | undefined {
    // Attribution data provides detailed debugging information
    const metricWithAttribution = metric as MetricWithAttribution;
    const attribution = metricWithAttribution.attribution;
    if (!attribution) return undefined;

    try {
      // Different metrics have different attribution structures
      switch (metric.name) {
        case 'LCP': {
          // LCP attribution includes the element that was painted
          const lcpAttribution = attribution as LCPAttribution;
          return lcpAttribution.element ? getElementSelector(lcpAttribution.element) : undefined;
        }

        case 'CLS': {
          // CLS attribution includes the largest shifting element
          const clsAttribution = attribution as CLSAttribution;
          return clsAttribution.largestShiftTarget ? getElementSelector(clsAttribution.largestShiftTarget) : undefined;
        }

        case 'INP': {
          // INP attribution includes the interaction target element
          const inpAttribution = attribution as INPAttribution;
          return inpAttribution.interactionTarget ? getElementSelector(inpAttribution.interactionTarget) : undefined;
        }

        default:
          return undefined;
      }
    } catch (error) {
      // If element extraction fails, just return undefined
      this._log('Failed to extract element from attribution', error);
      return undefined;
    }
  }

  /**
   * Setup page load metric tracking (not part of Core Web Vitals)
   */
  private _setupPageLoad(): void {
    if (typeof performance.getEntriesByType !== 'function') return;

    const reportPageLoad = () => {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length === 0) return;

      const nav = navEntries[0] as PerformanceNavigationTiming;
      const pageLoadMetric: PageLoadMetric = {
        load_time: nav.loadEventEnd - nav.fetchStart,
        dom_content_loaded_time: nav.domContentLoadedEventEnd - nav.fetchStart,
        timestamp: Date.now(),
      };

      this._onEvent({ type: TelemetryEventType.PAGE_LOAD, data: pageLoadMetric });
      this._log(
        `Page load: ${pageLoadMetric.load_time.toFixed(2)}ms, DOMContentLoaded: ${pageLoadMetric.dom_content_loaded_time.toFixed(2)}ms`
      );
    };

    if (document.readyState === 'complete') {
      reportPageLoad();
    } else {
      window.addEventListener('load', reportPageLoad, { once: true });
    }
  }
}
